import isEmpty from 'lodash/isEmpty';
import React, { forwardRef, useState } from 'react';
import { Tabs, TabData, Widget, DataView, SortableWidgetProps } from '@creditsights/styleguide-web';
import { useLocation } from 'react-router-dom';
import { ROUTES } from '../../../Base.constants';
import { useFeedFilters, useBreakpoint } from '../../../hooks/common';
import { Article, ContentTypeFilter, TopReadsRange } from '../../../models';
import useFetchTopReads from '../../../hooks/topReads/useFetchTopReads';
import { expandedWithoutSummary } from '../common/viewConfigs';
import { ArticleTableRow } from '../../../models/ArticleTableRow';

const getKey = (article: ArticleTableRow) => article.id;

interface TopReadsWidgetProps extends SortableWidgetProps {
  articleTypeFilter: ContentTypeFilter;
}

const TopReadsWidget = (props: TopReadsWidgetProps, ref): React.ReactElement => {
  const { pathname } = useLocation();
  const [range, setRange] = useState<TopReadsRange>(TopReadsRange.DAY);
  const { isTabletPortrait } = useBreakpoint();
  const { filters } = useFeedFilters();
  const { articleTypeFilter } = props;
  const {
    data: topReads = { [TopReadsRange.DAY]: [], [TopReadsRange.WEEK]: [], [TopReadsRange.MONTH]: [] },
    isLoading,
    isRefetching,
    isError,
    refetch,
  } = useFetchTopReads(pathname !== ROUTES.globalFeed, filters, articleTypeFilter);

  const renderTopReads = (topReads: ArticleTableRow[]) => {
    const config = expandedWithoutSummary(isTabletPortrait);
    return topReads.length > 0 ? (
      <DataView data={topReads} config={config} getKey={getKey} expanded />
    ) : (
      <div>No Top Reads.</div>
    );
  };

  const createTab = (
    label: string,
    articles: Article[],
    range: TopReadsRange,
    currentRange: TopReadsRange
  ): TabData => {
    return {
      label,
      active: currentRange === range,
      onClick: () => {
        setRange(range);
      },
      dataTestId: `top-reads-tab-${range.toLowerCase()}`,
    };
  };

  const createTabs = (dayTopReads, weekTopReads, monthTopReads, range): TabData[] => {
    const tabs: TabData[] = [];
    if (!isEmpty(dayTopReads)) {
      tabs.push(createTab('Today', dayTopReads, TopReadsRange.DAY, range));
    }
    if (!isEmpty(weekTopReads)) {
      tabs.push(createTab('Last 7 Days', weekTopReads, TopReadsRange.WEEK, range));
    }
    if (!isEmpty(monthTopReads)) {
      tabs.push(createTab('Last 30 Days', monthTopReads, TopReadsRange.MONTH, range));
    }
    return tabs;
  };

  return (
    <Widget
      headerTitle="Top Reads"
      firstLine={
        <Tabs
          tabs={createTabs(
            topReads[TopReadsRange.DAY],
            topReads[TopReadsRange.WEEK],
            topReads[TopReadsRange.MONTH],
            range
          )}
        />
      }
      isLoading={isLoading || isRefetching}
      isError={isError}
      onRetryAfterErrorClick={refetch}
      fixedHeight
      wrapFirstLine
      dataTestId="top-reads"
      ref={ref}
      {...props}
    >
      {filters ? renderTopReads(topReads[range]) : undefined}
    </Widget>
  );
};

export default forwardRef(TopReadsWidget);
